import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Input from '../../../../shared/_TBComponents/Input';
import Text from '../../../../shared/_TBComponents/Text';
import IconActionButton from './IconActionButton';
import { fetchProgramPreview } from '../../ducks/navigatorActions';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const PaymentWarningBanner = styled.div`
  background: #FFF3F3;
  border: 1px solid #FFBDBD;
  border-radius: 4px;
  padding: 10px 14px;
  display: flex;
  justify-content: center;
`;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ProgramListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(90vh - 310px);
`;

const ProgramCard = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isDropdown ? 'column' : 'row')};
  justify-content: space-between;
  align-items: ${(props) => (props.isDropdown ? 'stretch' : 'center')};
  width: 100%;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.isChecked ? '#0DCC8A' : '#D0D0D0')};
  background: ${(props) => (props.isChecked ? '#0DCC8A33' : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  margin-bottom: 12px;
  gap: 4px;
`;

const ProgramInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StatusAndText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProgramStatusCircle = styled.div`
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid ${(props) => props.color};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  padding-right: 4px;
`;

const InfoValue = styled.div`
  font-size: 12px;
  color: #333333;
  padding-right: 12px;
`;

const TagButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const InfoTag = styled.div`
  background: #EEEEEE;
  color: #333333;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ProgramCardHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const ArrowIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(props) => (props.isExpanded ? 90 : 0)}deg);
`;

const ExpandedContent = styled.div`
  width: 100%;
  min-width: 0;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SelectButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  z-index: 2;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #FFFFFF;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  flex: 1;
`;

const WeekNavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 16px;
  gap: 16px;
`;

const WeekNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.disabled ? '#DDD' : '#444')};
  background: white;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  padding: 0;
`;

const DayScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.dayCount <= 2 ? 'center' : 'flex-start')};
  overflow-x: ${(props) => (props.dayCount <= 2 ? 'hidden' : 'auto')};
  gap: 20px;
  padding-bottom: 20px;
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
`;

const DayPreviewCard = styled.div`
  flex-shrink: 0;
  width: 250px;
  background: #FFFFFF;
  border-radius: 4px;
  border: 1px solid #D0D0D0;
  box-shadow: 0px 1px 3px 0px rgba(48, 49, 51, 0.3);
  display: flex;
  flex-direction: column;
  height: 250px;
  overflow: hidden;
`;

const DayPreviewHeader = styled.div`
  padding: 6px 8px;
  border-bottom: 1px solid #EEEEEE;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  flex-shrink: 0;
  color: #444;
`;

const DayPreviewContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 8px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const ExerciseRow = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 32px;
  font-size: 12px;
  color: #333;
`;

const ExerciseIconAndLine = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  position: relative;
`;

const ExerciseVerticalLine = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  background: #D0D0D0;
  top: ${(props) => (props.isFirst ? '50%' : '0')};
  bottom: ${(props) => (props.isLast ? '50%' : '0')};
`;

const ExerciseIconCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #333;
  background: white;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const ExerciseTitle = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  align-self: center;
`;

const ButtonWrapper = styled.div`
  button:focus { outline: none; }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ShimmerDay = styled.div`
  flex-shrink: 0;
  width: 250px;
  height: 250px;
  border-radius: 4px;
  background: #EEEEEE;
  animation: shimmer 1.2s ease-in-out infinite;
  @keyframes shimmer {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const AthleteProgramForm = ({
  athletePrograms,
  programs,
  onOpenNestedModal,
  addProgramMode,
  onSelectionChange,
  fetchError,
  canAccessDayBasedPrograms,
}) => {
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const programPreviewDays = useSelector((state) => state.navigator.data.programPreviewDays);
  const isLoadingProgramPreview = useSelector(
    (state) => state.navigator.ui.isLoadingProgramPreview,
  );

  const [expandedProgramId, setExpandedProgramId] = useState(null);
  const [expandedWeekIndex, setExpandedWeekIndex] = useState(0);
  const [programSearch, setProgramSearch] = useState('');
  const [selectedProgramIds, setSelectedProgramIds] = useState(new Set());

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(addProgramMode ? Array.from(selectedProgramIds) : []);
    }
  }, [selectedProgramIds, addProgramMode]);

  const fetchErrorMessage = fetchError === true ? 'Unable to load programs. Please try again.' : fetchError;

  const assignedPrograms = athletePrograms || [];
  const assignedIds = new Set([
    ...(athletePrograms || []).map((p) => p.dayBasedProgramId),
    ...(athletePrograms || []).map((p) => p.saveInfoId),
  ]);

  const availablePrograms = programs.filter(
    (p) => !assignedIds.has(p.id)
      && !assignedIds.has(p.dayBasedProgramId)
      && !assignedIds.has(p.saveInfoId),
  );

  const filteredPrograms = availablePrograms.filter((p) => (
    p.programName?.toLowerCase().includes(programSearch.trim().toLowerCase())
  ));

  useEffect(() => {
    setExpandedWeekIndex(0);
  }, [expandedProgramId]);

  // Group program preview days into separate arrays for each week
  const weeklyDays = useMemo(() => {
    if (!programPreviewDays || programPreviewDays.length === 0) return [];
    const getWeekNum = (day, idx) => (
      day.weekNumber || Math.floor((day.dayNumber || idx) / 7) + 1
    );
    const maxWeek = Math.max(...programPreviewDays.map(getWeekNum));
    if (!maxWeek || Number.isNaN(maxWeek) || maxWeek < 1) return [programPreviewDays];
    const weekArray = Array.from({ length: maxWeek }, () => []);
    programPreviewDays.forEach((day, idx) => {
      const weekIdx = getWeekNum(day, idx) - 1;
      if (weekIdx >= 0 && weekIdx < maxWeek) weekArray[weekIdx].push(day);
    });
    return weekArray;
  }, [programPreviewDays]);

  const totalWeeks = weeklyDays.length;
  const currentWeekDays = weeklyDays[expandedWeekIndex] || [];

  const handleToggleExpand = (programId) => {
    const newExpanded = expandedProgramId === programId ? null : programId;
    setExpandedProgramId(newExpanded);
    if (newExpanded && accountCode) {
      dispatch(fetchProgramPreview(accountCode, newExpanded));
    }
  };

  if (addProgramMode) {
    return (
      <FormWrapper>
        <SearchRow>
          <SearchWrapper>
            <SearchIconWrapper>
              <Icon icon='magnifying-glass' size={16} color='#9E9E9E' />
            </SearchIconWrapper>
            <Input
              name='search-programs'
              inputOnly
              placeholder='Search Programs'
              value={programSearch}
              onChange={(e) => setProgramSearch(e.target.value)}
              styledPropsInput={{
                width: '100%',
                height: '40px',
                fontSize: '14px',
                fontWeight: 400,
                paddingLeft: '36px',
              }}
            />
          </SearchWrapper>
          <SelectButtonRow>
            <Button
              name='Select All'
              bgColor='white'
              borderColor='#ddd'
              txtColor='#333'
              customBorderRadius='4px'
              styledTextProps={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}
              styledWrapperProps={{
                height: '40px',
                padding: '6px 12px',
                minWidth: '115px',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                setSelectedProgramIds(new Set(filteredPrograms.map((p) => p.id)));
              }}
            />
            <Button
              name='Select None'
              bgColor='white'
              borderColor='#ddd'
              txtColor='#333'
              customBorderRadius='4px'
              styledTextProps={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}
              styledWrapperProps={{
                height: '40px',
                padding: '6px 12px',
                minWidth: '115px',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
              onClick={(e) => { e.preventDefault(); setSelectedProgramIds(new Set()); }}
            />
          </SelectButtonRow>
        </SearchRow>

        <ProgramListContainer>
          {filteredPrograms.length > 0 ? (
            filteredPrograms.map((program) => {
              const isExpanded = expandedProgramId === program.id;
              const isChecked = selectedProgramIds.has(program.id);
              const toggleCheck = () => {
                setSelectedProgramIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(program.id)) {
                    next.delete(program.id);
                  } else {
                    next.add(program.id);
                  }
                  return next;
                });
              };

              return (
                <ProgramCard key={program.id} isDropdown isChecked={isChecked}>
                  <ProgramCardHeader
                    role='button'
                    tabIndex={0}
                    onClick={() => handleToggleExpand(program.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleExpand(program.id);
                      }
                    }}
                    aria-expanded={isExpanded}
                  >
                    <CheckboxLabel
                      htmlFor={`program-check-${program.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CheckboxInput
                        id={`program-check-${program.id}`}
                        type='checkbox'
                        checked={isChecked}
                        onChange={toggleCheck}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <ProgramInfo>
                        <Text styledTextProps={{ fontSize: '14px', fontWeight: 600 }}>
                          {program.programName}
                        </Text>
                        <InfoRow>
                          {program.numberOfWeeks > 0 && (
                            <InfoValue>{`${program.numberOfWeeks} week${program.numberOfWeeks !== 1 ? 's' : ''}`}</InfoValue>
                          )}
                          {program.numberOfSessions > 0 && (
                            <InfoValue>{`${program.numberOfSessions} session${program.numberOfSessions !== 1 ? 's' : ''}`}</InfoValue>
                          )}
                        </InfoRow>
                      </ProgramInfo>
                    </CheckboxLabel>
                    <ArrowIconWrapper isExpanded={isExpanded} aria-hidden='true'>
                      <Icon icon='altrightarrow' size={18} color='#9E9E9E' />
                    </ArrowIconWrapper>
                  </ProgramCardHeader>

                  {isExpanded && (
                    <ExpandedContent>
                      {isLoadingProgramPreview ? (
                        <DayScrollContainer>
                          {[1, 2, 3].map((i) => <ShimmerDay key={i} />)}
                        </DayScrollContainer>
                      ) : (
                        <>
                          <WeekNavHeader>
                            <WeekNavButton
                              type='button'
                              disabled={expandedWeekIndex === 0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedWeekIndex((i) => Math.max(0, i - 1));
                              }}
                            >
                              <Icon
                                icon='left-arrow'
                                size={10}
                                color={expandedWeekIndex === 0 ? '#DDD' : '#444'}
                                styledProps={{ paddingBottom: '4px' }}
                              />
                            </WeekNavButton>
                            <Text styledTextProps={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                              {`Week ${expandedWeekIndex + 1} of ${totalWeeks}`}
                            </Text>
                            <WeekNavButton
                              type='button'
                              disabled={expandedWeekIndex >= totalWeeks - 1}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedWeekIndex((i) => Math.min(totalWeeks - 1, i + 1));
                              }}
                            >
                              <Icon
                                icon='right-arrow'
                                size={10}
                                color={expandedWeekIndex >= totalWeeks - 1 ? '#DDD' : '#444'}
                                styledProps={{ paddingBottom: '4px' }}
                              />
                            </WeekNavButton>
                          </WeekNavHeader>
                          <DayScrollContainer dayCount={currentWeekDays.length}>
                            {currentWeekDays.length > 0 ? (
                              currentWeekDays.map((day) => (
                                <DayPreviewCard key={day.dayNumber}>
                                  <DayPreviewHeader>
                                    {`Day ${day.dayInWeek || ((day.dayNumber || 0) % 7) + 1}`}
                                  </DayPreviewHeader>
                                  <DayPreviewContent>
                                    {(day.exercises || []).map((ex, idx, arr) => {
                                      const isFirst = idx === 0;
                                      const isLast = idx === arr.length - 1;
                                      return (
                                        <ExerciseRow key={ex.assignedId || ex.exerciseId || idx}>
                                          <ExerciseIconAndLine>
                                            <ExerciseVerticalLine
                                              isFirst={isFirst}
                                              isLast={isLast}
                                            />
                                            <ExerciseIconCircle>
                                              <Icon icon={ex.icon} size={12} color='#333' />
                                            </ExerciseIconCircle>
                                          </ExerciseIconAndLine>
                                          <ExerciseTitle>{ex.title}</ExerciseTitle>
                                        </ExerciseRow>
                                      );
                                    })}
                                  </DayPreviewContent>
                                </DayPreviewCard>
                              ))
                            ) : (
                              <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
                                No workout days found
                              </Text>
                            )}
                          </DayScrollContainer>
                        </>
                      )}
                    </ExpandedContent>
                  )}
                </ProgramCard>
              );
            })
          ) : (
            <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
              {programSearch ? 'No programs match your search' : 'All programs are already assigned to this athlete'}
            </Text>
          )}
        </ProgramListContainer>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <TabHeader>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 600 }}>
          Program Library Access
        </Text>
        <ButtonWrapper>
          <Button
            name='Add Program'
            icon='add-program'
            bgColor='white'
            borderColor='#ddd'
            txtColor='#333'
            customBorderRadius='4px'
            fullWidth
            styledTextProps={{
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}
            onClick={(e) => { e.preventDefault(); onOpenNestedModal('addProgram'); }}
          />
        </ButtonWrapper>
      </TabHeader>

      {canAccessDayBasedPrograms === false && (
        <PaymentWarningBanner>
          <Text styledTextProps={{ fontSize: '14px', color: '#CC0404' }}>
            This athlete does not currently have access to their assigned programs
            due to a failed payment in OS.
          </Text>
        </PaymentWarningBanner>
      )}

      <ProgramListContainer>
        {assignedPrograms && assignedPrograms.length > 0 ? (
          assignedPrograms.map((program) => (
            <ProgramCard key={program.saveInfoId}>
              <ProgramInfo>
                <Text styledTextProps={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                  {program.programName}
                </Text>
                <StatusAndText>
                  <ProgramStatusCircle color={program.statusColorCode || '#757575'} />
                  <InfoValue>{program.status || 'Not Started'}</InfoValue>
                </StatusAndText>
                <InfoRow>
                  <InfoLabel>Last Activity:</InfoLabel>
                  <InfoValue>{program.lastActivity || 'No activity yet'}</InfoValue>
                  <InfoLabel>Current Session:</InfoLabel>
                  <InfoValue>
                    {program.currentWeekDisplayValue && program.currentDayDisplayValue
                      ? `Week ${program.currentWeekDisplayValue} - ${program.currentDayDisplayValue}`
                      : 'Not started'}
                  </InfoValue>
                </InfoRow>
              </ProgramInfo>
              <TagButtonWrapper>
                {program.groupName && (
                <InfoTag>{`Assigned via group: ${program.groupName}`}</InfoTag>
                )}
                <ButtonContainer>
                  {program.status !== 'Not Started' && (
                  <IconActionButton
                    type='restart'
                    onClick={(e) => { e.preventDefault(); onOpenNestedModal('restartProgram', null, program); }}
                  />
                  )}
                  {!program.groupName && (
                  <IconActionButton
                    type='delete'
                    onClick={(e) => { e.preventDefault(); onOpenNestedModal('deleteProgram', null, program); }}
                  />
                  )}
                </ButtonContainer>
              </TagButtonWrapper>
            </ProgramCard>
          ))
        ) : (
          <Text styledTextProps={{ fontSize: '16px', color: fetchError ? '#CC0404' : '#9E9E9E' }}>
            {fetchError ? fetchErrorMessage : 'No programs assigned to this athlete'}
          </Text>
        )}
      </ProgramListContainer>
    </FormWrapper>
  );
};

AthleteProgramForm.propTypes = {
  onOpenNestedModal: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
  addProgramMode: PropTypes.bool,
  fetchError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  canAccessDayBasedPrograms: PropTypes.bool,
  athletePrograms: PropTypes.arrayOf(PropTypes.shape({
    dayBasedProgramId: PropTypes.number,
    dayBasedInstanceId: PropTypes.number,
    saveInfoId: PropTypes.number,
    programName: PropTypes.string,
    description: PropTypes.string,
    groupId: PropTypes.number,
    groupName: PropTypes.string,
    startedAt: PropTypes.string,
    lastActivity: PropTypes.string,
    currentDayRawValue: PropTypes.number,
    currentDayDisplayValue: PropTypes.string,
    currentWeekDisplayValue: PropTypes.number,
    totalNumberOfDays: PropTypes.number,
    numberOfActiveDays: PropTypes.number,
    numberOfWeeks: PropTypes.number,
    status: PropTypes.string,
    statusColorCode: PropTypes.string,
    isActive: PropTypes.bool,
  })),
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    accountCode: PropTypes.number,
    programName: PropTypes.string,
    description: PropTypes.string,
    numberOfDays: PropTypes.number,
    numberOfSessions: PropTypes.number,
    numberOfWeeks: PropTypes.number,
  })),
};

AthleteProgramForm.defaultProps = {
  addProgramMode: false,
  fetchError: false,
  athletePrograms: [],
  programs: [],
  onSelectionChange: null,
  canAccessDayBasedPrograms: null,
};

export default AthleteProgramForm;
